/**
 * Fixed Assets Register Page
 * Comprehensive asset management with depreciation tracking
 */

import React, { useState, useEffect } from 'react'
import { Plus, Search, Filter, MoreVertical, Package, Wrench, FileText, Edit, Trash2, Calendar, TrendingDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import AssetsService, { FixedAsset, AssetSummary, DepreciationSchedule, MaintenanceLog, AssetCategory, DepreciationMethod, PaymentMethod } from '@/services/assetsService'
import { Progress } from '@/components/ui/progress'
import { supabase } from '@/lib/supabase'

interface FixedAssetsProps {
  user: AdminUser | null
}

interface ChartOfAccount {
  id: string
  account_code: string
  account_name: string
  account_type: string
}

interface Vendor {
  id: string
  vendor_code: string
  vendor_name: string
}

interface Profile {
  id: string
  first_name: string
  last_name: string
}

interface AssetCategoryMapping {
  id: string
  asset_category: string
  asset_account_id: string
  accumulated_depreciation_account_id: string
  depreciation_expense_account_id: string
}

export default function FixedAssets({ user }: FixedAssetsProps) {
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [showDialog, setShowDialog] = useState(false)
  const [editingAsset, setEditingAsset] = useState<FixedAsset | null>(null)
  const [selectedAsset, setSelectedAsset] = useState<AssetSummary | null>(null)
  const [depreciationSchedule, setDepreciationSchedule] = useState<DepreciationSchedule[]>([])
  const [maintenanceLog, setMaintenanceLog] = useState<MaintenanceLog[]>([])
  const [showDetails, setShowDetails] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false)
  const [maintenanceFormData, setMaintenanceFormData] = useState({
    maintenance_date: new Date().toISOString().split('T')[0],
    maintenance_type: 'routine',
    description: '',
    performed_by: '',
    cost: '',
    status: 'completed',
    next_maintenance_date: '',
    parts_replaced: '',
    labor_hours: '',
    notes: '',
  })

  // Lookup data
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([])
  const [bankAccounts, setBankAccounts] = useState<ChartOfAccount[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [categoryMappings, setCategoryMappings] = useState<AssetCategoryMapping[]>([])

  // Form data
  const [formData, setFormData] = useState({
    asset_name: '',
    asset_category: 'equipment' as AssetCategory,
    asset_description: '',
    serial_number: '',
    model_number: '',
    manufacturer: '',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_cost: '',
    salvage_value: '0',
    depreciation_method: 'straight_line' as DepreciationMethod,
    useful_life_years: '5',
    depreciation_start_date: new Date().toISOString().split('T')[0],
    location: '',
    department: '',
    assigned_to: '',
    vendor_id: '',
    asset_account_id: '',
    depreciation_expense_account_id: '',
    accumulated_depreciation_account_id: '',
    // Payment fields
    payment_method: '' as PaymentMethod | '',
    payment_reference: '',
    payment_date: '',
    bank_account_id: '',
    credit_terms: '',
    payment_due_date: '',
    // Other fields
    is_insured: false,
    insurance_policy_number: '',
    insurance_value: '',
    insurance_expiry_date: '',
    warranty_expiry_date: '',
    notes: '',
    status: 'active' as const,
    is_active: true,
  })

  useEffect(() => {
    if (user?.churchId) {
      loadAssets()
      loadLookupData()
    }
  }, [user, statusFilter, categoryFilter])

  async function loadLookupData() {
    if (!user?.churchId) return

    try {
      // Load chart of accounts
      const { data: accountsData } = await supabase
        .from('chart_of_accounts')
        .select('id, account_code, account_name, account_type')
        .eq('church_id', user.churchId)
        .eq('is_active', true)
        .order('account_code')

      setAccounts(accountsData || [])

      // Load bank/cash accounts for payment tracking
      const { data: bankAccountsData } = await supabase
        .from('chart_of_accounts')
        .select('id, account_code, account_name, account_type')
        .eq('church_id', user.churchId)
        .in('account_type', ['asset', 'cash', 'bank'])
        .eq('is_active', true)
        .order('account_code')

      setBankAccounts(bankAccountsData || [])

      // Load vendors
      const { data: vendorsData } = await supabase
        .from('vendors')
        .select('id, vendor_code, vendor_name')
        .eq('church_id', user.churchId)
        .eq('is_active', true)
        .order('vendor_name')

      setVendors(vendorsData || [])

      // Load profiles (for assignment)
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .order('first_name')

      setProfiles(profilesData || [])

      // Load asset category mappings for automatic account assignment
      const { data: categoryMappingsData } = await supabase
        .from('asset_category_gl_mapping')
        .select('id, asset_category, asset_account_id, accumulated_depreciation_account_id, depreciation_expense_account_id')
        .eq('church_id', user.churchId)

      setCategoryMappings(categoryMappingsData || [])
    } catch (error) {
      console.error('Error loading lookup data:', error)
    }
  }

  async function loadAssets() {
    if (!user?.churchId) return

    try {
      setLoading(true)
      const filters: any = {}

      if (statusFilter !== 'all') {
        filters.status = statusFilter as any
      }

      if (categoryFilter !== 'all') {
        filters.category = categoryFilter as any
      }

      if (searchTerm) {
        filters.search = searchTerm
      }

      const data = await AssetsService.getAssets(user.churchId, filters)
      setAssets(data)
    } catch (error) {
      console.error('Error loading assets:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleViewDetails(asset: AssetSummary) {
    setSelectedAsset(asset)
    try {
      const [schedule, maintenance] = await Promise.all([
        AssetsService.getAssetDepreciationSchedule(asset.id),
        AssetsService.getAssetMaintenanceLog(asset.id)
      ])
      setDepreciationSchedule(schedule)
      setMaintenanceLog(maintenance)
      setShowDetails(true)
    } catch (error) {
      console.error('Error loading asset details:', error)
    }
  }

  function handleAddMaintenance(asset: AssetSummary) {
    setSelectedAsset(asset)
    // Reset maintenance form
    setMaintenanceFormData({
      maintenance_date: new Date().toISOString().split('T')[0],
      maintenance_type: 'routine',
      description: '',
      performed_by: '',
      cost: '',
      status: 'completed',
      next_maintenance_date: '',
      parts_replaced: '',
      labor_hours: '',
      notes: '',
    })
    setShowMaintenanceDialog(true)
  }

  async function handleSaveMaintenance() {
    if (!user?.churchId || !user.id || !selectedAsset) return

    // Validation
    if (!maintenanceFormData.maintenance_date) {
      alert('Error: Please enter maintenance date')
      return
    }
    if (!maintenanceFormData.description.trim()) {
      alert('Error: Please enter description')
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('asset_maintenance_log')
        .insert({
          church_id: user.churchId,
          asset_id: selectedAsset.id,
          maintenance_date: maintenanceFormData.maintenance_date,
          maintenance_type: maintenanceFormData.maintenance_type,
          description: maintenanceFormData.description,
          performed_by: maintenanceFormData.performed_by || null,
          cost: maintenanceFormData.cost ? parseFloat(maintenanceFormData.cost) : 0,
          status: maintenanceFormData.status,
          next_maintenance_date: maintenanceFormData.next_maintenance_date || null,
          parts_replaced: maintenanceFormData.parts_replaced || null,
          labor_hours: maintenanceFormData.labor_hours ? parseFloat(maintenanceFormData.labor_hours) : null,
          notes: maintenanceFormData.notes || null,
          created_by: user.id,
        })

      if (error) throw error

      alert('Success: Maintenance log added successfully')
      setShowMaintenanceDialog(false)

      // Refresh maintenance log if details dialog is open
      if (showDetails) {
        const maintenance = await AssetsService.getAssetMaintenanceLog(selectedAsset.id)
        setMaintenanceLog(maintenance)
      }

      // Reload assets to get updated maintenance cost
      loadAssets()
    } catch (error) {
      console.error('Error saving maintenance:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save maintenance log'}`)
    } finally {
      setSaving(false)
    }
  }

  function handleAddAsset() {
    setEditingAsset(null)
    // Reset form to defaults
    setFormData({
      asset_name: '',
      asset_category: 'equipment',
      asset_description: '',
      serial_number: '',
      model_number: '',
      manufacturer: '',
      purchase_date: new Date().toISOString().split('T')[0],
      purchase_cost: '',
      salvage_value: '0',
      depreciation_method: 'straight_line',
      useful_life_years: '5',
      depreciation_start_date: new Date().toISOString().split('T')[0],
      location: '',
      department: '',
      assigned_to: '',
      vendor_id: '',
      asset_account_id: '',
      depreciation_expense_account_id: '',
      accumulated_depreciation_account_id: '',
      // Payment fields
      payment_method: '',
      payment_reference: '',
      payment_date: '',
      bank_account_id: '',
      credit_terms: '',
      payment_due_date: '',
      // Other fields
      is_insured: false,
      insurance_policy_number: '',
      insurance_value: '',
      insurance_expiry_date: '',
      warranty_expiry_date: '',
      notes: '',
      status: 'active',
      is_active: true,
    })
    setShowDialog(true)
  }

  function handleCategoryChange(category: AssetCategory) {
    // Update the category
    setFormData(prev => ({ ...prev, asset_category: category }))

    // Look up the mapping for this category
    const mapping = categoryMappings.find(m => m.asset_category === category)

    if (mapping) {
      // Auto-fill the three account fields
      setFormData(prev => ({
        ...prev,
        asset_category: category,
        asset_account_id: mapping.asset_account_id || prev.asset_account_id,
        depreciation_expense_account_id: mapping.depreciation_expense_account_id || prev.depreciation_expense_account_id,
        accumulated_depreciation_account_id: mapping.accumulated_depreciation_account_id || prev.accumulated_depreciation_account_id
      }))

      // Show success message if at least one account was mapped
      if (mapping.asset_account_id || mapping.depreciation_expense_account_id || mapping.accumulated_depreciation_account_id) {
        console.log('Accounts automatically assigned based on category')
      }
    }
  }

  function handleEditAsset(asset: FixedAsset) {
    setEditingAsset(asset)
    // Populate form with asset data
    setFormData({
      asset_name: asset.asset_name,
      asset_category: asset.asset_category,
      asset_description: asset.asset_description || '',
      serial_number: asset.serial_number || '',
      model_number: asset.model_number || '',
      manufacturer: asset.manufacturer || '',
      purchase_date: asset.purchase_date,
      purchase_cost: asset.purchase_cost.toString(),
      salvage_value: asset.salvage_value.toString(),
      depreciation_method: asset.depreciation_method,
      useful_life_years: asset.useful_life_years?.toString() || '5',
      depreciation_start_date: asset.depreciation_start_date || asset.purchase_date,
      location: asset.location || '',
      department: asset.department || '',
      assigned_to: asset.assigned_to || '',
      vendor_id: asset.vendor_id || '',
      asset_account_id: asset.asset_account_id,
      depreciation_expense_account_id: asset.depreciation_expense_account_id || '',
      accumulated_depreciation_account_id: asset.accumulated_depreciation_account_id || '',
      is_insured: asset.is_insured,
      insurance_policy_number: asset.insurance_policy_number || '',
      insurance_value: asset.insurance_value?.toString() || '',
      insurance_expiry_date: asset.insurance_expiry_date || '',
      warranty_expiry_date: asset.warranty_expiry_date || '',
      notes: asset.notes || '',
      status: asset.status,
      is_active: asset.is_active,
    })
    setShowDialog(true)
  }

  async function handleSaveAsset() {
    if (!user?.churchId || !user.id) return

    // Validation
    if (!formData.asset_name.trim()) {
      alert('Error: Please enter asset name')
      return
    }
    if (!formData.purchase_cost || parseFloat(formData.purchase_cost) <= 0) {
      alert('Error: Please enter valid purchase cost')
      return
    }
    if (!formData.asset_account_id) {
      alert('Error: Please select an asset account')
      return
    }

    // Payment validation for new assets
    if (!editingAsset) {
      if (!formData.payment_method) {
        alert('Error: Please select payment method')
        return
      }
      if (formData.payment_method !== 'credit_purchase' && !formData.bank_account_id) {
        alert('Error: Please select bank/cash account for payment')
        return
      }
    }

    // Helper to convert placeholder values to undefined
    const toOptional = (value: string) => (!value || value === 'none' || value === 'unassigned') ? undefined : value

    setSaving(true)
    try {
      if (editingAsset) {
        // Update existing asset
        await AssetsService.updateAsset(
          editingAsset.id,
          {
            asset_name: formData.asset_name,
            asset_category: formData.asset_category,
            asset_description: formData.asset_description || undefined,
            serial_number: formData.serial_number || undefined,
            model_number: formData.model_number || undefined,
            manufacturer: formData.manufacturer || undefined,
            purchase_date: formData.purchase_date,
            purchase_cost: parseFloat(formData.purchase_cost),
            salvage_value: parseFloat(formData.salvage_value) || 0,
            depreciation_method: formData.depreciation_method,
            useful_life_years: formData.useful_life_years ? parseInt(formData.useful_life_years) : undefined,
            depreciation_start_date: formData.depreciation_start_date || undefined,
            location: formData.location || undefined,
            department: formData.department || undefined,
            assigned_to: toOptional(formData.assigned_to),
            vendor_id: toOptional(formData.vendor_id),
            asset_account_id: formData.asset_account_id,
            depreciation_expense_account_id: toOptional(formData.depreciation_expense_account_id),
            accumulated_depreciation_account_id: toOptional(formData.accumulated_depreciation_account_id),
            is_insured: formData.is_insured,
            insurance_policy_number: formData.insurance_policy_number || undefined,
            insurance_value: formData.insurance_value ? parseFloat(formData.insurance_value) : undefined,
            insurance_expiry_date: formData.insurance_expiry_date || undefined,
            warranty_expiry_date: formData.warranty_expiry_date || undefined,
            notes: formData.notes || undefined,
            status: formData.status,
            is_active: formData.is_active,
          },
          user.id
        )
        alert('Success: Asset updated successfully')
      } else {
        // Create new asset
        const purchaseCost = parseFloat(formData.purchase_cost)
        await AssetsService.createAsset(
          user.churchId,
          {
            asset_name: formData.asset_name,
            asset_category: formData.asset_category,
            asset_description: formData.asset_description || undefined,
            serial_number: formData.serial_number || undefined,
            model_number: formData.model_number || undefined,
            manufacturer: formData.manufacturer || undefined,
            purchase_date: formData.purchase_date,
            purchase_cost: purchaseCost,
            salvage_value: parseFloat(formData.salvage_value) || 0,
            depreciation_method: formData.depreciation_method,
            useful_life_years: formData.useful_life_years ? parseInt(formData.useful_life_years) : undefined,
            depreciation_start_date: formData.depreciation_start_date || undefined,
            location: formData.location || undefined,
            department: formData.department || undefined,
            assigned_to: toOptional(formData.assigned_to),
            vendor_id: toOptional(formData.vendor_id),
            asset_account_id: formData.asset_account_id,
            depreciation_expense_account_id: toOptional(formData.depreciation_expense_account_id),
            accumulated_depreciation_account_id: toOptional(formData.accumulated_depreciation_account_id),
            // Payment fields
            payment_method: formData.payment_method as PaymentMethod,
            payment_reference: formData.payment_reference || undefined,
            payment_date: formData.payment_date || formData.purchase_date,
            bank_account_id: toOptional(formData.bank_account_id),
            credit_terms: formData.credit_terms || undefined,
            payment_due_date: formData.payment_due_date || undefined,
            amount_paid: formData.payment_method === 'credit_purchase' ? 0 : purchaseCost,
            amount_outstanding: formData.payment_method === 'credit_purchase' ? purchaseCost : 0,
            // Other fields
            is_insured: formData.is_insured,
            insurance_policy_number: formData.insurance_policy_number || undefined,
            insurance_value: formData.insurance_value ? parseFloat(formData.insurance_value) : undefined,
            insurance_expiry_date: formData.insurance_expiry_date || undefined,
            warranty_expiry_date: formData.warranty_expiry_date || undefined,
            notes: formData.notes || undefined,
            status: formData.status,
            is_active: formData.is_active,
          },
          user.id
        )
        alert('Success: Asset created successfully with journal entry')
      }

      setShowDialog(false)
      loadAssets()
    } catch (error) {
      console.error('Error saving asset:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save asset'}`)
    } finally {
      setSaving(false)
    }
  }

  const filteredAssets = assets.filter(asset =>
    searchTerm === '' ||
    asset.asset_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.asset_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Calculate summary stats
  const totalCost = assets.reduce((sum, a) => sum + a.purchase_cost, 0)
  const totalCurrentValue = assets.reduce((sum, a) => sum + a.current_value, 0)
  const totalDepreciation = assets.reduce((sum, a) => sum + a.accumulated_depreciation, 0)

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Fixed Assets Register</h1>
            <p className="text-sm text-muted-foreground">
              Track assets with depreciation (FASB ASC 958 & IAS 16 compliant)
            </p>
          </div>
          <Button onClick={handleAddAsset}>
            <Plus className="mr-2 h-4 w-4" />
            Add Asset
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Filters */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disposed">Disposed</SelectItem>
                <SelectItem value="under_maintenance">Under Maintenance</SelectItem>
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="land">Land</SelectItem>
                <SelectItem value="building">Building</SelectItem>
                <SelectItem value="vehicle">Vehicle</SelectItem>
                <SelectItem value="furniture">Furniture</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="computer">Computer</SelectItem>
                <SelectItem value="musical_instrument">Musical Instrument</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{assets.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Active assets in register
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Purchase Cost</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(totalCost, 'GHS')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total acquisition cost
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Value</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(totalCurrentValue, 'GHS')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Net book value
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Accumulated Depreciation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(totalDepreciation, 'GHS')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total depreciation
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Assets Table */}
        <Card>
          <CardHeader>
            <CardTitle>Assets Register</CardTitle>
            <CardDescription>
              {filteredAssets.length} assets found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Purchase Cost</TableHead>
                    <TableHead className="text-right">Current Value</TableHead>
                    <TableHead className="text-right">Depreciation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.map((asset) => (
                    <TableRow key={asset.id}>
                      <TableCell className="font-medium">{asset.asset_code}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{asset.asset_name}</div>
                          {asset.serial_number && (
                            <div className="text-xs text-muted-foreground">
                              SN: {asset.serial_number}
                            </div>
                          )}
                          {asset.assigned_to_name && (
                            <div className="text-xs text-muted-foreground">
                              Assigned to: {asset.assigned_to_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{asset.asset_category.replace('_', ' ')}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(asset.purchase_cost, 'GHS')}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(asset.current_value, 'GHS')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="space-y-1">
                          <div>{formatCurrency(asset.accumulated_depreciation, 'GHS')}</div>
                          <Progress value={asset.depreciation_percentage} className="h-1" />
                          <div className="text-xs text-muted-foreground">
                            {asset.depreciation_percentage.toFixed(1)}%
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            asset.status === 'active'
                              ? 'default'
                              : asset.status === 'disposed'
                              ? 'secondary'
                              : 'destructive'
                          }
                        >
                          {asset.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetails(asset)}>
                              <FileText className="mr-2 h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditAsset(asset)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAddMaintenance(asset)}>
                              <Wrench className="mr-2 h-4 w-4" />
                              Add Maintenance
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Calendar className="mr-2 h-4 w-4" />
                              Depreciation Schedule
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Asset Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Asset Details - {selectedAsset?.asset_name}</DialogTitle>
            <DialogDescription>
              {selectedAsset?.asset_code} | {selectedAsset?.asset_category}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="info">Information</TabsTrigger>
              <TabsTrigger value="depreciation">Depreciation</TabsTrigger>
              <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h4 className="font-semibold mb-2">Purchase Information</h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Purchase Date:</dt>
                      <dd className="font-medium">{new Date(selectedAsset?.purchase_date || '').toLocaleDateString()}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Purchase Cost:</dt>
                      <dd className="font-medium">{formatCurrency(selectedAsset?.purchase_cost || 0, 'GHS')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Salvage Value:</dt>
                      <dd className="font-medium">{formatCurrency(selectedAsset?.salvage_value || 0, 'GHS')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Vendor:</dt>
                      <dd className="font-medium">{selectedAsset?.vendor_name || '-'}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Current Status</h4>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Current Value:</dt>
                      <dd className="font-medium">{formatCurrency(selectedAsset?.current_value || 0, 'GHS')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Accumulated Depreciation:</dt>
                      <dd className="font-medium">{formatCurrency(selectedAsset?.accumulated_depreciation || 0, 'GHS')}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Age:</dt>
                      <dd className="font-medium">{selectedAsset?.age_years} years</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Assigned To:</dt>
                      <dd className="font-medium">{selectedAsset?.assigned_to_name || 'Unassigned'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Location:</dt>
                      <dd className="font-medium">{selectedAsset?.location || '-'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="depreciation">
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3 mb-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Depreciation Method</div>
                      <div className="text-lg font-semibold capitalize">
                        {selectedAsset?.depreciation_method.replace('_', ' ')}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Useful Life</div>
                      <div className="text-lg font-semibold">
                        {selectedAsset?.useful_life_years} years
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Monthly Depreciation</div>
                      <div className="text-lg font-semibold">
                        {formatCurrency(
                          AssetsService.calculateStraightLineDepreciation(
                            selectedAsset?.purchase_cost || 0,
                            selectedAsset?.salvage_value || 0,
                            selectedAsset?.useful_life_years || 1
                          ),
                          'GHS'
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Opening</TableHead>
                      <TableHead className="text-right">Depreciation</TableHead>
                      <TableHead className="text-right">Closing</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {depreciationSchedule.slice(0, 12).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{new Date(item.depreciation_date).toLocaleDateString()}</TableCell>
                        <TableCell>{item.fiscal_year}-{item.fiscal_period.toString().padStart(2, '0')}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.opening_value, 'GHS')}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.depreciation_amount, 'GHS')}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.closing_value, 'GHS')}</TableCell>
                        <TableCell>
                          <Badge variant={item.status === 'posted' ? 'default' : 'secondary'}>
                            {item.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="maintenance">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Performed By</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {maintenanceLog.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{new Date(log.maintenance_date).toLocaleDateString()}</TableCell>
                      <TableCell className="capitalize">{log.maintenance_type}</TableCell>
                      <TableCell>{log.description}</TableCell>
                      <TableCell>{log.performed_by || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(log.cost, 'GHS')}</TableCell>
                      <TableCell>
                        <Badge variant={log.status === 'completed' ? 'default' : 'secondary'}>
                          {log.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Asset Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
            <DialogDescription>
              {editingAsset ? 'Update asset information' : 'Create a new fixed asset with depreciation tracking'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="depreciation">Depreciation</TabsTrigger>
              <TabsTrigger value="other">Other</TabsTrigger>
            </TabsList>

            {/* Basic Information */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="asset_name">Asset Name *</Label>
                  <Input
                    id="asset_name"
                    value={formData.asset_name}
                    onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                    placeholder="Enter asset name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="asset_category">Category *</Label>
                  <Select
                    value={formData.asset_category}
                    onValueChange={(value) => handleCategoryChange(value as AssetCategory)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="building">Building</SelectItem>
                      <SelectItem value="vehicle">Vehicle</SelectItem>
                      <SelectItem value="furniture">Furniture</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                      <SelectItem value="computer">Computer</SelectItem>
                      <SelectItem value="musical_instrument">Musical Instrument</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Selecting a category will automatically assign the appropriate accounts
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serial_number">Serial Number</Label>
                  <Input
                    id="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="Enter serial number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model_number">Model Number</Label>
                  <Input
                    id="model_number"
                    value={formData.model_number}
                    onChange={(e) => setFormData({ ...formData, model_number: e.target.value })}
                    placeholder="Enter model number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                    placeholder="Enter manufacturer"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendor_id">Vendor</Label>
                  <Select
                    value={formData.vendor_id}
                    onValueChange={(value) => setFormData({ ...formData, vendor_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.vendor_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset_description">Description</Label>
                <Textarea
                  id="asset_description"
                  value={formData.asset_description}
                  onChange={(e) => setFormData({ ...formData, asset_description: e.target.value })}
                  placeholder="Enter asset description"
                  rows={3}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Main Sanctuary"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    placeholder="e.g., Worship"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="assigned_to">Assigned To</Label>
                  <Select
                    value={formData.assigned_to}
                    onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.first_name} {profile.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Financial Information */}
            <TabsContent value="financial" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="purchase_date">Purchase Date *</Label>
                  <Input
                    id="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_cost">Purchase Cost (GHS) *</Label>
                  <Input
                    id="purchase_cost"
                    type="number"
                    step="0.01"
                    value={formData.purchase_cost}
                    onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="salvage_value">Salvage Value (GHS)</Label>
                  <Input
                    id="salvage_value"
                    type="number"
                    step="0.01"
                    value={formData.salvage_value}
                    onChange={(e) => setFormData({ ...formData, salvage_value: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="warranty_expiry_date">Warranty Expiry</Label>
                  <Input
                    id="warranty_expiry_date"
                    type="date"
                    value={formData.warranty_expiry_date}
                    onChange={(e) => setFormData({ ...formData, warranty_expiry_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset_account_id">
                  Asset Account *
                  {formData.asset_account_id && categoryMappings.find(m => m.asset_category === formData.asset_category && m.asset_account_id === formData.asset_account_id) && (
                    <span className="ml-2 text-xs text-green-600 font-normal">✓ Auto-filled</span>
                  )}
                </Label>
                <Select
                  value={formData.asset_account_id}
                  onValueChange={(value) => setFormData({ ...formData, asset_account_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select asset account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts
                      .filter((a) => a.account_type === 'asset')
                      .map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.account_code} - {account.account_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Account where the asset value will be recorded. Auto-filled based on category, but can be changed if needed.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="depreciation_expense_account_id">
                  Depreciation Expense Account
                  {formData.depreciation_expense_account_id && formData.depreciation_expense_account_id !== 'none' && categoryMappings.find(m => m.asset_category === formData.asset_category && m.depreciation_expense_account_id === formData.depreciation_expense_account_id) && (
                    <span className="ml-2 text-xs text-green-600 font-normal">✓ Auto-filled</span>
                  )}
                </Label>
                <Select
                  value={formData.depreciation_expense_account_id}
                  onValueChange={(value) => setFormData({ ...formData, depreciation_expense_account_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select expense account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accounts
                      .filter((a) => a.account_type === 'expense')
                      .map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.account_code} - {account.account_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Account where depreciation expense will be posted. Auto-filled based on category, but can be changed if needed.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accumulated_depreciation_account_id">
                  Accumulated Depreciation Account
                  {formData.accumulated_depreciation_account_id && formData.accumulated_depreciation_account_id !== 'none' && categoryMappings.find(m => m.asset_category === formData.asset_category && m.accumulated_depreciation_account_id === formData.accumulated_depreciation_account_id) && (
                    <span className="ml-2 text-xs text-green-600 font-normal">✓ Auto-filled</span>
                  )}
                </Label>
                <Select
                  value={formData.accumulated_depreciation_account_id}
                  onValueChange={(value) => setFormData({ ...formData, accumulated_depreciation_account_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select accumulated depreciation account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accounts
                      .filter((a) => a.account_type === 'asset')
                      .map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.account_code} - {account.account_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Contra-asset account for accumulated depreciation. Auto-filled based on category, but can be changed if needed.
                </p>
              </div>

              {/* Payment Information Section */}
              {!editingAsset && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4">Payment Information</h3>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="payment_method">Payment Method *</Label>
                        <Select
                          value={formData.payment_method}
                          onValueChange={(value) => setFormData({ ...formData, payment_method: value as PaymentMethod })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="check">Check</SelectItem>
                            <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                            <SelectItem value="credit_card">Credit Card</SelectItem>
                            <SelectItem value="credit_purchase">Credit Purchase (Pay Later)</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          How was this asset paid for?
                        </p>
                      </div>

                      {/* Bank Account (for non-credit purchases) */}
                      {formData.payment_method && formData.payment_method !== 'credit_purchase' && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="bank_account_id">Bank/Cash Account *</Label>
                            <Select
                              value={formData.bank_account_id}
                              onValueChange={(value) => setFormData({ ...formData, bank_account_id: value })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select account" />
                              </SelectTrigger>
                              <SelectContent>
                                {bankAccounts.map((account) => (
                                  <SelectItem key={account.id} value={account.id}>
                                    {account.account_code} - {account.account_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Account from which payment was made
                            </p>
                          </div>

                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="payment_reference">Payment Reference</Label>
                              <Input
                                id="payment_reference"
                                value={formData.payment_reference}
                                onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                                placeholder="Check #, Transfer Ref, etc."
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="payment_date">Payment Date</Label>
                              <Input
                                id="payment_date"
                                type="date"
                                value={formData.payment_date || formData.purchase_date}
                                onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                              />
                            </div>
                          </div>

                          <div className="rounded-md bg-blue-50 p-4">
                            <p className="text-sm text-blue-900">
                              <strong>Journal Entry:</strong> DR {accounts.find(a => a.id === formData.asset_account_id)?.account_name || 'Asset Account'} / CR {bankAccounts.find(a => a.id === formData.bank_account_id)?.account_name || 'Bank Account'}
                            </p>
                          </div>
                        </>
                      )}

                      {/* Credit Purchase Fields */}
                      {formData.payment_method === 'credit_purchase' && (
                        <>
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="credit_terms">Credit Terms</Label>
                              <Input
                                id="credit_terms"
                                value={formData.credit_terms}
                                onChange={(e) => setFormData({ ...formData, credit_terms: e.target.value })}
                                placeholder="e.g., Net 30, Net 60"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="payment_due_date">Payment Due Date</Label>
                              <Input
                                id="payment_due_date"
                                type="date"
                                value={formData.payment_due_date}
                                onChange={(e) => setFormData({ ...formData, payment_due_date: e.target.value })}
                              />
                            </div>
                          </div>

                          <div className="rounded-md bg-orange-50 p-4">
                            <p className="text-sm text-orange-900">
                              <strong>Note:</strong> This will be recorded as a credit purchase.<br />
                              <strong>Journal Entry:</strong> DR {accounts.find(a => a.id === formData.asset_account_id)?.account_name || 'Asset Account'} / CR Accounts Payable
                              <br />You can record payments later in the asset details.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Depreciation Settings */}
            <TabsContent value="depreciation" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="depreciation_method">Depreciation Method</Label>
                  <Select
                    value={formData.depreciation_method}
                    onValueChange={(value) => setFormData({ ...formData, depreciation_method: value as DepreciationMethod })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="straight_line">Straight Line</SelectItem>
                      <SelectItem value="declining_balance">Declining Balance</SelectItem>
                      <SelectItem value="units_of_production">Units of Production</SelectItem>
                      <SelectItem value="none">No Depreciation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="useful_life_years">Useful Life (Years)</Label>
                  <Input
                    id="useful_life_years"
                    type="number"
                    min="1"
                    value={formData.useful_life_years}
                    onChange={(e) => setFormData({ ...formData, useful_life_years: e.target.value })}
                    disabled={formData.depreciation_method === 'none'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="depreciation_start_date">Depreciation Start Date</Label>
                  <Input
                    id="depreciation_start_date"
                    type="date"
                    value={formData.depreciation_start_date}
                    onChange={(e) => setFormData({ ...formData, depreciation_start_date: e.target.value })}
                    disabled={formData.depreciation_method === 'none'}
                  />
                </div>
              </div>

              {formData.depreciation_method === 'straight_line' && formData.purchase_cost && formData.useful_life_years && (
                <Card>
                  <CardContent className="pt-6">
                    <h4 className="text-sm font-semibold mb-2">Monthly Depreciation Calculation</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Purchase Cost:</span>
                        <span>{formatCurrency(parseFloat(formData.purchase_cost), 'GHS')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Salvage Value:</span>
                        <span>{formatCurrency(parseFloat(formData.salvage_value || '0'), 'GHS')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Depreciable Amount:</span>
                        <span>
                          {formatCurrency(
                            parseFloat(formData.purchase_cost) - parseFloat(formData.salvage_value || '0'),
                            'GHS'
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Useful Life:</span>
                        <span>{formData.useful_life_years} years ({parseInt(formData.useful_life_years) * 12} months)</span>
                      </div>
                      <div className="flex justify-between font-semibold pt-2 border-t">
                        <span>Monthly Depreciation:</span>
                        <span>
                          {formatCurrency(
                            AssetsService.calculateStraightLineDepreciation(
                              parseFloat(formData.purchase_cost),
                              parseFloat(formData.salvage_value || '0'),
                              parseInt(formData.useful_life_years)
                            ),
                            'GHS'
                          )}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Other Information */}
            <TabsContent value="other" className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_insured"
                    checked={formData.is_insured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_insured: checked as boolean })}
                  />
                  <Label htmlFor="is_insured">Asset is insured</Label>
                </div>

                {formData.is_insured && (
                  <div className="grid gap-4 md:grid-cols-3 pl-6">
                    <div className="space-y-2">
                      <Label htmlFor="insurance_policy_number">Policy Number</Label>
                      <Input
                        id="insurance_policy_number"
                        value={formData.insurance_policy_number}
                        onChange={(e) => setFormData({ ...formData, insurance_policy_number: e.target.value })}
                        placeholder="Enter policy number"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="insurance_value">Insured Value (GHS)</Label>
                      <Input
                        id="insurance_value"
                        type="number"
                        step="0.01"
                        value={formData.insurance_value}
                        onChange={(e) => setFormData({ ...formData, insurance_value: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="insurance_expiry_date">Expiry Date</Label>
                      <Input
                        id="insurance_expiry_date"
                        type="date"
                        value={formData.insurance_expiry_date}
                        onChange={(e) => setFormData({ ...formData, insurance_expiry_date: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes or comments"
                    rows={4}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="under_maintenance">Under Maintenance</SelectItem>
                        <SelectItem value="disposed">Disposed</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                        <SelectItem value="stolen">Stolen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2 pt-6">
                    <Checkbox
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                    />
                    <Label htmlFor="is_active">Active in system</Label>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveAsset} disabled={saving}>
              {saving ? 'Saving...' : editingAsset ? 'Update Asset' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Maintenance Log Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Maintenance Log</DialogTitle>
            <DialogDescription>
              Record maintenance performed on {selectedAsset?.asset_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="maintenance_date">Maintenance Date *</Label>
                <Input
                  id="maintenance_date"
                  type="date"
                  value={maintenanceFormData.maintenance_date}
                  onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, maintenance_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maintenance_type">Type *</Label>
                <Select
                  value={maintenanceFormData.maintenance_type}
                  onValueChange={(value) => setMaintenanceFormData({ ...maintenanceFormData, maintenance_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="preventive">Preventive</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={maintenanceFormData.description}
                onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, description: e.target.value })}
                placeholder="Describe the maintenance work performed"
                rows={3}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="performed_by">Performed By</Label>
                <Input
                  id="performed_by"
                  value={maintenanceFormData.performed_by}
                  onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, performed_by: e.target.value })}
                  placeholder="Technician or company name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost">Cost (GHS)</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  value={maintenanceFormData.cost}
                  onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={maintenanceFormData.status}
                  onValueChange={(value) => setMaintenanceFormData({ ...maintenanceFormData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="next_maintenance_date">Next Maintenance Date</Label>
                <Input
                  id="next_maintenance_date"
                  type="date"
                  value={maintenanceFormData.next_maintenance_date}
                  onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, next_maintenance_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="parts_replaced">Parts Replaced</Label>
                <Input
                  id="parts_replaced"
                  value={maintenanceFormData.parts_replaced}
                  onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, parts_replaced: e.target.value })}
                  placeholder="List of parts replaced"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="labor_hours">Labor Hours</Label>
                <Input
                  id="labor_hours"
                  type="number"
                  step="0.1"
                  value={maintenanceFormData.labor_hours}
                  onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, labor_hours: e.target.value })}
                  placeholder="0.0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={maintenanceFormData.notes}
                onChange={(e) => setMaintenanceFormData({ ...maintenanceFormData, notes: e.target.value })}
                placeholder="Any additional notes or observations"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintenanceDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveMaintenance} disabled={saving}>
              {saving ? 'Saving...' : 'Save Maintenance Log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
